import { useNavigate } from 'react-router-dom'
import type { CSSProperties } from 'react'
import { cn } from '@/utils/cn'
import { formatDashboardCurrency } from '@/utils/crmValuesDisplay'
import type { JourneyMacroAggregate } from '@/components/Dashboard/operacaoTab.utils'

type Props = {
  steps: JourneyMacroAggregate[]
  pipelineCount: number
  pipelineValue: number
  valuesVisible?: boolean
}

function PipelineColumn({
  step,
  onClick,
  valuesVisible,
}: {
  step: JourneyMacroAggregate
  onClick: () => void
  valuesVisible: boolean
}) {
  const { macro, count, value } = step

  return (
    <button
      type='button'
      onClick={onClick}
      className={cn(
        'flex min-w-[9rem] flex-1 flex-col rounded-2xl border border-[var(--border)] bg-[var(--bg-dark)]/10 p-3 text-left transition-all hover:shadow-sm',
        count === 0 && 'opacity-60',
      )}
      style={{ '--stage-color': macro.color } as CSSProperties}
    >
      <div className='flex items-center justify-between gap-2'>
        <span className='flex items-center gap-2 text-xs font-semibold text-text-secondary'>
          <span
            className='h-2 w-2 rounded-full'
            style={{ backgroundColor: macro.color }}
            aria-hidden
          />
          {macro.label}
        </span>
        <span className='rounded-full bg-[var(--bg-card)] px-2 py-0.5 text-[10px] font-bold tabular-nums text-text-muted'>
          {count}
        </span>
      </div>

      <div className='mt-4 min-h-[72px] rounded-xl border border-dashed border-[var(--border)] bg-[var(--bg-card)] p-3'>
        {count > 0 ? (
          <>
            <p className='text-2xl font-bold tabular-nums text-text-primary'>{count}</p>
            <p className='mt-1 text-[11px] text-text-muted'>
              {formatDashboardCurrency(value, valuesVisible)}
            </p>
          </>
        ) : (
          <p className='text-xs text-text-muted'>Sem deals</p>
        )}
      </div>
    </button>
  )
}

export function OperacaoJourneyFunnel({
  steps,
  pipelineCount,
  pipelineValue,
  valuesVisible = false,
}: Props) {
  const navigate = useNavigate()
  const openDeals = () => navigate('/deals')

  return (
    <div className='rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-5 shadow-sm'>
      <div className='mb-4 flex flex-wrap items-end justify-between gap-3'>
        <div>
          <h3 className='text-base font-semibold text-text-primary'>Pipeline board</h3>
          <p className='mt-1 text-sm text-text-muted'>
            Visão por macro-etapa do funil comercial
          </p>
        </div>
        <div className='text-right'>
          <p className='text-xs text-text-muted'>Valor total</p>
          <p className='text-sm font-bold tabular-nums text-text-primary'>
            {formatDashboardCurrency(pipelineValue, valuesVisible)}
          </p>
        </div>
      </div>

      {pipelineCount > 0 ? (
        <div className='flex gap-3 overflow-x-auto pb-1'>
          {steps.map((step) => (
            <PipelineColumn
              key={step.macro.id}
              step={step}
              onClick={openDeals}
              valuesVisible={valuesVisible}
            />
          ))}
        </div>
      ) : (
        <div className='rounded-xl border border-dashed border-[var(--border)] px-4 py-8 text-center text-sm text-text-muted'>
          Nenhuma negociação ativa no pipeline.
        </div>
      )}
    </div>
  )
}
